/* Form Component to Add or Edit Customer details*/

import { useContext, useEffect, useState } from "react";
import { Button, Form, Input, Row, Col } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { TContext, TPrivilege, TResponseModel } from "../../../types/config";
import { ePrivileges, eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { TCustomer } from "../../../types";
import {
  AddEditCustomer,
  GetSpecificCustomerData,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import { UserContext } from "../../../context";
import "../../../index.css";
import { requiredFieldValidator } from "../../../utils/input.validator";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";

type DrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * CustomerForm component is used for adding or editing customer information.
 * It provides a form with fields for customer details such as name, code,
 * The component handles fetching and setting dropdown options for countries, states,
 * cities, and regions based on the selected values. It also manages form
 * submission to add or update customer records.
 *
 * Props:
 * - id: number - The ID of the customer to edit; 0 for adding a new customer.
 * - onCloseDrawer: function - Callback function to close the form drawer.
 * - onRefreshList: function - Callback function to refresh the customer list
 *   after adding or editing a customer.
 */
export default function CustomerForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const context: TContext = useContext(UserContext);

  const { post } = useFetch();
  const { onShowToast } = useToast();

  const defaultValues: TCustomer = {
    id: 0,
    name: "",
    customerCode: "",
  };

  // Hook to handle on load operations
  useEffect(() => {
    /**
     * fetchData is an asynchronous function that fetches customer data, countries, states, and regions.
     * It first fetches specific customer data if ID is provided, followed by fetching countries, then states, and finally regions.
     */
    const fetchData = async () => {
      await fetchSpecificCustomer(); // Fetch specific customer if ID is provided
    };

    fetchData(); // Call the fetch function
  }, []);

  // Handle on submit event
  const onFinish = async (values: TCustomer) => {
    try {
      const payload = {
        data: {
          id: props.id ? props.id : 0,
          ...values,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditCustomer, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        props.onCloseDrawer();
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // To fetch specific config param
  const fetchSpecificCustomer = async () => {
    if (props.id > 0) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response: TResponseModel = await post(
          GetSpecificCustomerData,
          payload
        );
        if (response) {
          const { data, dataResponse } = response;
          const { returnCode, description } = dataResponse;
          if (returnCode === eResultCode.SUCCESS) {
            form.setFieldsValue({ ...data });
          } else {
            onShowToast({
              type: ToastType.error,
              title: <CloseCircleFilled />,
              position: ToastOpen.leftBottom,
              content: description,
            });
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <Row
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Col>
              <h3 style={{ color: "var(--bs-dark)", marginTop: "10px" }}>
                {`${props.id ? "Edit" : "Add"} a Customer`}
              </h3>
            </Col>
            <Col>
              <Button
                type="default"
                loading={isLoading}
                disabled={isLoading}
                onClick={() => {
                  props.onCloseDrawer();
                }}
              >
                Close
              </Button>
            </Col>
          </Row>
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("Enter name"),
                  },
                ]}
              >
                <Input
                  maxLength={100}
                  disabled={isLoading}
                  autoComplete="off"
                  placeholder="Enter customer name"
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="customerCode"
                label="Code"
                rules={[
                  { required: true, message: "Please enter code" },
                  {
                    pattern: /^[a-zA-Z0-9]{2}$/, // Regular expression for exactly 2 alphanumeric characters
                    message: "Code must be 2 alphanumeric characters",
                  },
                ]}
              >
                <Input
                  autoComplete="off"
                  disabled={
                    Utils.isUserHasAccess(
                      (context?.user?.privilegeList as TPrivilege[]) || [],
                      ePrivileges.EDITCUSTCODE
                    ) || !isLoading
                      ? false
                      : true
                  }
                  placeholder="Enter code"
                  maxLength={2}
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase(); // Convert input to uppercase
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              size="large"
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              loading={isLoading}
              className="btnDimensions"
            >
              {props.id ? "Update" : "Save"}
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
